/**
 * Floating textarea overlay for the prompt → chat morph.
 *
 * Rendered once, above the router/Suspense, so the element persists across the
 * route change and the workspace's code-split load (no blank flash). Driven by
 * the {@link usePromptMorph} store: the source page places it at the prompt's
 * rect, the workspace moves it onto the chat input and fades it out.
 *
 * @module
 */

import type { CSSProperties } from 'react'

import { usePromptMorph } from '../lib/promptMorph.js'

const GEOM =
  'left 560ms cubic-bezier(0.4,0,0.2,1), top 560ms cubic-bezier(0.4,0,0.2,1),' +
  ' width 560ms cubic-bezier(0.4,0,0.2,1), height 560ms cubic-bezier(0.4,0,0.2,1)'

/**
 * Renders the morphing textarea, or nothing when no morph is in flight.
 *
 * @returns The overlay element, or `null`.
 */
export function PromptMorphOverlay() {
  const s = usePromptMorph()
  if (!s.active || !s.rect) return null

  // Mobile soft-keyboard belt: fixed positioning anchors to the LAYOUT
  // viewport, but with the keyboard open (or just dismissing) the user sees the
  // VISUAL viewport, which can sit offsetTop/offsetLeft pixels into it. Shift
  // by that offset so the overlay lands where the source rect is actually seen.
  // Desktop (and a settled mobile viewport) reports 0/0 — a no-op.
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  const offsetLeft = vv?.offsetLeft ?? 0
  const offsetTop = vv?.offsetTop ?? 0

  const style: CSSProperties = {
    position: 'fixed',
    left: s.rect.left + offsetLeft,
    top: s.rect.top + offsetTop,
    width: s.rect.width,
    height: s.rect.height,
    opacity: s.fading ? 0 : 1,
    transition: `${s.animate ? `${GEOM}, ` : ''}opacity 240ms ease`,
    zIndex: 9999,
    pointerEvents: 'none',
    boxSizing: 'border-box',
    // Match the Synthase chat input container so the hand-off is seamless.
    padding: '8px 10px',
    borderRadius: 8,
    background: 'var(--mol-color-surface, #161616)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
    overflow: 'hidden',
    color: 'var(--mol-color-text, #e0e0e0)',
    fontFamily: 'Arimo, sans-serif',
    fontSize: 14,
    lineHeight: 1.5,
  }

  // mol-grad-border draws the animated gradient ring — the flourish belongs on
  // the textarea, not the chat card.
  return (
    <div className="mol-grad-border" style={style} aria-hidden="true">
      <div
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {s.text}
      </div>
    </div>
  )
}
